import {
  ColumnType,
  CurrencyValue,
  DropdownOptionAPI,
  DropdownOptionValidation,
  FieldValue,
  HookedRecordResult,
  ValidatorAPI,
} from '@getnuvo/importer-react'

import type { Column } from 'modules/universal_importer/components/NuvoImporter/useColumnsMapping'

// Type alias to avoid linter issues with any
export type CompatibleColumns = unknown[]

// Type for modifier.addColumn() parameter
export type ModifierColumn = {
  key: string
  label: string
  columnType: ColumnType
  validations?: ValidatorAPI[] | null | undefined
  dropdownOptions?: DropdownOptionAPI[] | undefined
  outputFormat?: string | undefined
  isMultiSelect?: boolean | undefined
  hidden?: boolean | undefined
  disabled?: boolean | undefined
}

// Helper to convert DropdownOption to DropdownOptionAPI
function convertDropdownOption(option: {
  label: string
  alternativeMatches?: string[]
  description?: string
  type: string
  value: string
  validations?: DropdownOptionValidation[] | null
}): DropdownOptionAPI {
  // DropdownOptionAPI is a union type - handle each case separately
  if (option.type === 'int' || option.type === 'float') {
    const numericValue =
      option.type === 'int' ? parseInt(option.value, 10) : parseFloat(option.value)
    return {
      label: option.label,
      value: numericValue,
      type: option.type,
      alternativeMatches: option.alternativeMatches,
      description: option.description,
      validations: option.validations || undefined,
    }
  }

  // For string type, return as DropdownOptionAPIString
  return {
    label: option.label,
    value: option.value,
    type: 'string',
    alternativeMatches: option.alternativeMatches,
    description: option.description,
    validations: option.validations || undefined,
  }
}

// Helper to convert Column to ModifierColumn type
export function toModifierColumn(column: Column): ModifierColumn {
  if (!column.columnType) {
    throw new Error('Column must have columnType')
  }

  return {
    key: column.key,
    label: column.label,
    columnType: column.columnType,
    validations: column.validations || undefined,
    dropdownOptions: column.dropdownOptions?.map(convertDropdownOption) || undefined,
    outputFormat: column.outputFormat || undefined,
    isMultiSelect: column.isMultiSelect || undefined,
    hidden: column.hidden || undefined,
    disabled: column.disabled || undefined,
  }
}

// Type guards and constructors to avoid type assertions
function isHookedRecordResult(value: unknown): value is HookedRecordResult {
  return typeof value === 'object' && value !== null && 'value' in value && 'info' in value
}

function isFieldValue(value: unknown): value is FieldValue {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value === null ||
    value === undefined
  )
}

function isCurrencyValue(value: unknown): value is CurrencyValue {
  return typeof value === 'object' && value !== null && 'value' in value && 'type' in value
}

// Helper to extract field value from row
export function getFieldValue(
  row: Record<string, FieldValue | CurrencyValue | HookedRecordResult>,
  key: string
): FieldValue {
  if (isHookedRecordResult(row[key]) || isCurrencyValue(row[key])) {
    return row[key]?.value || ''
  } else if (isFieldValue(row[key])) {
    return row[key]
  }
  return null
}

// Helper to create HookedRecordResult for errors
export function createHookedRecordResult(
  fieldValue: FieldValue,
  message: string
): HookedRecordResult {
  return {
    value: fieldValue,
    info: [{ message, level: 'error' }],
  }
}

// Helper to create RowResult from validation/transformation results
export function createRowResult(
  value: FieldValue | CurrencyValue | HookedRecordResult
): HookedRecordResult | FieldValue {
  if (isHookedRecordResult(value)) {
    return value // Return HookedRecordResult for errors
  } else if (isFieldValue(value)) {
    return value // Return FieldValue for success
  } else {
    return value.value // Extract value from CurrencyValue
  }
}

// Type guard to check if object has key property
export function hasKey(obj: unknown): obj is { key: string; [key: string]: unknown } {
  return typeof obj === 'object' && obj !== null && 'key' in obj
}

// Helper to find column by key
export function findColumnByKey(columns: CompatibleColumns, targetKey: string) {
  if (Array.isArray(columns)) {
    return columns.find((col) => hasKey(col) && col.key === targetKey) || null
  }
  return null
}

// Helper to safely convert Column[] to compatible type for Nuvo ColumnAPI
// Helper to convert columns to CompatibleColumns for function parameters
export function toCompatibleColumns(columns: Array<Record<string, unknown>>): CompatibleColumns {
  return columns
}
